import React, { useState, useEffect } from 'react';
import { buildSitePageInfoUrl, getSitePageInfoHeaders } from './config';

interface BlogItem {
  id: number;
  infoName: string;
  infoHeader: string;
  inforDetails: string;
  infoType: string;
  tags: string;
  mainimage: string;
  createdDate: string;
}

const Blogs: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedBlog, setSelectedBlog] = useState<BlogItem | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      
      // Try the direct API call first
      try {
        const response = await fetch(buildSitePageInfoUrl('Blogs'), {
          method: 'GET',
          headers: getSitePageInfoHeaders(),
          mode: 'cors'
        });
        
        if (response.ok) {
          const data = await response.json();
          setBlogs(data);
          return;
        }
      } catch (corsError) {
        console.log('CORS error, trying alternative approach:', corsError);
      }
      
      // Fallback: Use mock data if API is not accessible
      const mockData = [
        {
          id: 1,
          infoName: "App Development Best Practices",
          infoHeader: "Building scalable and maintainable applications",
          inforDetails: "This is a sample blog post about app development best practices. It covers modern development techniques and industry standards.",
          infoType: "Development",
          tags: "app-dev,best-practices,coding",
          mainimage: "",
          createdDate: new Date().toISOString()
        },
        {
          id: 2,
          infoName: "Mobile App Development Trends",
          infoHeader: "Latest trends in mobile application development",
          inforDetails: "Explore the latest trends and technologies in mobile app development, including cross-platform solutions and native development approaches.",
          infoType: "Mobile",
          tags: "mobile,trends,development",
          mainimage: "",
          createdDate: new Date().toISOString()
        }
      ];
      
      setBlogs(mockData);
      setError("Using sample data - API connection failed due to CORS policy. Please configure CORS on your API server.");
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  // Filter blogs based on search term
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.infoName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.infoHeader.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.inforDetails.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Strip HTML tags from content
  const stripHtml = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  // Truncate text
  const truncateText = (text: string, maxLength: number = 200) => {
    const stripped = stripHtml(text);
    return stripped.length > maxLength ? stripped.substring(0, maxLength) + '...' : stripped;
  };

  // Handle card click to show full details
  const handleCardClick = (blog: BlogItem) => {
    setSelectedBlog(blog);
    setShowDetails(true);
  };

  // Close details view and go back to grid
  const goBackToGrid = () => {
    setShowDetails(false);
    setSelectedBlog(null);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          {/* Loading Spinner */}
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2c5aa0',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          
          {/* Loading Text */}
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            Loading Knowledge Base...
          </div>
          
          {/* Animated Dots */}
          <div style={{
            fontSize: '1.5rem',
            letterSpacing: '2px',
            animation: 'dots 1.5s infinite'
          }}>
            • • •
          </div>
        </div>
        
        {/* CSS Animation Styles */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes dots {
            0%, 20% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'left', 
        padding: '40px',
        color: '#d32f2f',
        fontSize: '1.1rem'
      }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ padding: window.innerWidth < 480 ? '20px 16px' : '40px 20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Arial, sans-serif', textAlign: 'left' }}>
      <h1 style={{ 
        textAlign: 'left', 
        marginBottom: '40px', 
        color: '#333',
        fontSize: window.innerWidth < 480 ? '1.5rem' : window.innerWidth < 768 ? '2rem' : '2.5rem',
        fontWeight: 'bold'
      }}>
        Blogs and News
      </h1>
      
      {showDetails && selectedBlog ? (
        // Detailed Blog View
        <div>
          {/* Back Button */}
          <button
            onClick={goBackToGrid}
            style={{
              marginBottom: '30px',
              padding: window.innerWidth < 480 ? '8px 16px' : '12px 24px',
              backgroundColor: '#2c5aa0',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1e3d72';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2c5aa0';
            }}
          >
            ← Back to Blogs
          </button>

          {/* Blog Content */}
          <article style={{ 
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0'
          }}>
            {/* Title */}
            <h1 style={{
              color: '#2c5aa0',
              marginBottom: '20px',
              fontSize: '2.5rem',
              fontWeight: '700',
              lineHeight: '1.2',
              textAlign: 'left'
            }}>
              {selectedBlog.infoName}
            </h1>

            {/* Header/Subtitle */}
            <h2 style={{
              color: '#444',
              marginBottom: '32px',
              fontSize: '1.4rem',
              fontWeight: '500',
              lineHeight: '1.4',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '16px',
              textAlign: 'left'
            }}>
              {selectedBlog.infoHeader}
            </h2>

            {/* Main Image */}
            {selectedBlog.mainimage && selectedBlog.mainimage.trim() !== '' && (
              <div style={{ marginBottom: '32px' }}>
                <img 
                  src={`https://admn.terasite.com/${selectedBlog.mainimage}`}
                  alt={selectedBlog.infoName}
                  style={{
                    width: '100%',
                    maxHeight: '400px',
                    objectFit: 'cover',
                    borderRadius: '12px'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.parentElement) {
                      e.currentTarget.parentElement.style.display = 'none';
                    }
                  }}
                />
              </div>
            )}

            {/* Full Content */}
            <div
              className="blog-content"
              style={{
                color: '#333',
                lineHeight: '1.8',
                fontSize: '1.1rem',
                marginBottom: '40px',
                textAlign: 'left'
              }}
              dangerouslySetInnerHTML={{ __html: selectedBlog.inforDetails }}
            />

            {/* Inline styles for content formatting */}
            <style>{`
              .blog-content ul {
                margin: 16px 0 !important;
                padding-left: 24px !important;
                list-style-type: disc !important;
              }
              
              .blog-content ul ul {
                margin: 8px 0 !important;
                padding-left: 20px !important;
                list-style-type: circle !important;
              }
              
              .blog-content ol {
                margin: 16px 0 !important;
                padding-left: 24px !important;
                list-style-type: decimal !important;
              }
              
              .blog-content li {
                margin: 8px 0 !important;
                padding-left: 4px !important;
                line-height: 1.6 !important;
              }
              
              .blog-content li p {
                margin: 4px 0 !important;
              }

              .blog-content dl {
                margin: 20px 0 !important;
                padding: 16px !important;
                background-color: #f8f9fa !important;
                border-radius: 8px !important;
                border-left: 4px solid #2c5aa0 !important;
              }

              .blog-content dt {
                font-weight: 600 !important;
                color: #2c5aa0 !important;
                margin: 12px 0 4px 0 !important;
                font-size: 1rem !important;
                display: inline-block !important;
                min-width: 120px !important;
              }

              .blog-content dd {
                margin: 0 0 12px 0 !important;
                padding-left: 8px !important;
                color: #333 !important;
                font-size: 1rem !important;
                display: inline-block !important;
                margin-left: 8px !important;
              }

              .blog-content dt:first-of-type {
                margin-top: 0 !important;
              }

              .blog-content dd:last-of-type {
                margin-bottom: 0 !important;
              }

              .blog-content h1, .blog-content h2, .blog-content h3, 
              .blog-content h4, .blog-content h5, .blog-content h6 {
                margin: 24px 0 16px 0 !important;
                font-weight: 600 !important;
                color: #2c5aa0 !important;
              }

              .blog-content p {
                margin: 12px 0 !important;
                line-height: 1.7 !important;
              }

              .blog-content blockquote {
                margin: 16px 0 !important;
                padding: 16px 20px !important;
                border-left: 4px solid #2c5aa0 !important;
                background-color: #f8f9fa !important;
                font-style: italic !important;
              }

              .blog-content code {
                background-color: #f1f1f1 !important;
                padding: 2px 6px !important;
                border-radius: 4px !important;
                font-family: 'Courier New', monospace !important;
                font-size: 0.9em !important;
              }

              .blog-content pre {
                background-color: #f8f9fa !important;
                padding: 16px !important;
                border-radius: 8px !important;
                overflow-x: auto !important;
                margin: 16px 0 !important;
              }

              .blog-content a {
                color: #2c5aa0 !important;
                text-decoration: underline !important;
              }

              .blog-content a:hover {
                color: #1e3d72 !important;
              }

              .blog-content strong, .blog-content b {
                font-weight: 600 !important;
              }

              .blog-content em, .blog-content i {
                font-style: italic !important;
              }

              .blog-content table {
                width: 100% !important;
                border-collapse: collapse !important;
                margin: 16px 0 !important;
                background-color: #fff !important;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
              }

              .blog-content th, .blog-content td {
                padding: 12px !important;
                text-align: left !important;
                border-bottom: 1px solid #e0e0e0 !important;
              }

              .blog-content th {
                background-color: #f8f9fa !important;
                font-weight: 600 !important;
                color: #2c5aa0 !important;
              }

              .blog-content tr:hover {
                background-color: #f8f9fa !important;
              }
            `}</style>

            {/* Tags Section */}
            {selectedBlog.tags && (
              <div style={{ 
                marginBottom: '32px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <h3 style={{ 
                  color: '#666', 
                  marginBottom: '16px', 
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  textAlign: 'left'
                }}>
                  Related Topics:
                </h3>
                <div>
                  {selectedBlog.tags.split(',').map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#2c5aa0',
                        color: '#fff',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        marginRight: '12px',
                        marginBottom: '8px',
                        fontWeight: '500'
                      }}
                    >
                      #{tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Blog Meta Information */}
            <div style={{
              borderTop: '2px solid #e0e0e0',
              paddingTop: '24px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              color: '#666',
              fontSize: '1rem'
            }}>
              <div>
                <strong>Published Date:</strong><br />
                {new Date(selectedBlog.createdDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div>
                <strong>Blog ID:</strong><br />
                #{selectedBlog.id}
              </div>
              <div>
                <strong>Category:</strong><br />
                {selectedBlog.infoType}
              </div>
            </div>
          </article>
        </div>
      ) : (
        // Grid View
        <>
          {/* Search and Filter Section */}
          <div style={{ 
            marginBottom: '30px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '20px',
            alignItems: 'flex-start'
          }}>
            {/* Search Bar */}
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '12px 16px',
                fontSize: '1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '25px',
                width: '100%',
                maxWidth: '400px',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#2c5aa0'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* View Mode Toggle */}
          <div style={{ 
            marginBottom: '20px', 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '10px'
          }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '8px 16px',
                border: viewMode === 'grid' ? '2px solid #2c5aa0' : '2px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: viewMode === 'grid' ? '#2c5aa0' : '#fff',
                color: viewMode === 'grid' ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}
            >
              📱 Grid View
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                border: viewMode === 'list' ? '2px solid #2c5aa0' : '2px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: viewMode === 'list' ? '#2c5aa0' : '#fff',
                color: viewMode === 'list' ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}
            >
              📋 List View
            </button>
          </div>

          {/* Results Count */}
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'left', 
            color: '#666',
            fontSize: '1rem'
          }}>
            Showing {filteredBlogs.length} blog{filteredBlogs.length !== 1 ? 's' : ''}
          </div>
          
          {/* Blog Items - Grid or List View */}
          {viewMode === 'grid' ? (
            // Grid View
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
              gap: '25px' 
            }}>
              {filteredBlogs.map((blog) => (
                <div 
                  key={blog.id}
                  onClick={() => handleCardClick(blog)}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '24px',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Category Badge */}
                  {/* <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    backgroundColor: '#2c5aa0',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                  }}>
                    {blog.infoType}
                  </div> */}

                  {/* Main Image */}
                  {blog.mainimage && blog.mainimage.trim() !== '' && (
                    <div style={{ marginBottom: '16px' }}>
                      <img 
                        src={blog.mainimage}
                        alt={blog.infoName}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.style.display = 'none';
                          }
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Title */}
                  <h3 style={{ 
                    color: '#2c5aa0', 
                    marginBottom: '12px',
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    lineHeight: '1.4',
                    marginTop: blog.mainimage ? '0' : '30px',
                    textAlign: 'left'
                  }}>
                    {blog.infoName}
                  </h3>
                  
                  {/* Header/Subtitle */}
                  <h4 style={{
                    color: '#444',
                    marginBottom: '12px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    lineHeight: '1.4',
                    textAlign: 'left'
                  }}>
                    {blog.infoHeader}
                  </h4>
                  
                  {/* Tags */}
                  {blog.tags && (
                    <div style={{ marginBottom: '16px' }}>
                      {blog.tags.split(',').slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          style={{
                            display: 'inline-block',
                            backgroundColor: '#f0f0f0',
                            color: '#666',
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            marginRight: '6px',
                            marginBottom: '6px'
                          }}
                        >
                          #{tag.trim()}
                        </span>
                      ))}
                      {blog.tags.split(',').length > 3 && (
                        <span style={{ fontSize: '0.8rem', color: '#999' }}>
                          +{blog.tags.split(',').length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Date */}
                  <div style={{
                    color: '#999',
                    fontSize: '0.85rem',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '12px'
                  }}>
                    {new Date(blog.createdDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // List View
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '16px'
            }}>
              {filteredBlogs.map((blog) => (
                <div 
                  key={blog.id}
                  onClick={() => handleCardClick(blog)}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'flex-start'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Image Section */}
                  {blog.mainimage && blog.mainimage.trim() !== '' && (
                    <div style={{ flexShrink: 0 }}>
                      <img 
                        src={blog.mainimage}
                        alt={blog.infoName}
                        style={{
                          width: '120px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.style.display = 'none';
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Content Section */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                      {/* Title */}
                      <h3 style={{ 
                        color: '#2c5aa0', 
                        margin: '0 16px 0 0',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        lineHeight: '1.4',
                        flex: 1,
                        textAlign: 'left'
                      }}>
                        {blog.infoName}
                      </h3>
                      
                      {/* Category Badge */}
                      {/* <div style={{
                        backgroundColor: '#2c5aa0',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        flexShrink: 0
                      }}>
                        {blog.infoType}
                      </div> */}
                    </div>
                    
                    {/* Header/Subtitle */}
                    <h4 style={{
                      color: '#444',
                      margin: '0 0 8px 0',
                      fontSize: '1rem',
                      fontWeight: '500',
                      lineHeight: '1.4',
                      textAlign: 'left'
                    }}>
                      {blog.infoHeader}
                    </h4>
                    
                    {/* Footer with Tags and Date */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {/* Tags */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {blog.tags && blog.tags.split(',').slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            style={{
                              backgroundColor: '#f0f0f0',
                              color: '#666',
                              padding: '2px 8px',
                              borderRadius: '8px',
                              fontSize: '0.75rem'
                            }}
                          >
                            #{tag.trim()}
                          </span>
                        ))}
                        {blog.tags && blog.tags.split(',').length > 2 && (
                          <span style={{ fontSize: '0.75rem', color: '#999' }}>
                            +{blog.tags.split(',').length - 2} more
                          </span>
                        )}
                      </div>

                      {/* Date */}
                      <div style={{
                        color: '#999',
                        fontSize: '0.8rem'
                      }}>
                        {new Date(blog.createdDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results Message */}
          {filteredBlogs.length === 0 && (
            <div style={{ 
              textAlign: 'left', 
              padding: '60px 20px',
              color: '#666',
              fontSize: '1.1rem'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔍</div>
              <div>No blogs found matching your criteria.</div>
              <div style={{ fontSize: '0.9rem', marginTop: '10px' }}>
                Try adjusting your search terms or category filter.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Blogs;